/**
 * Sanctology — strict opt-in consent state library
 * -------------------------------------------------
 * Single source of truth for cookie / tracker consent. Stores the visitor's
 * choices in localStorage, broadcasts changes through a tiny event bus, and
 * fire-and-forget POSTs an audit record to /api/consent on every decision.
 *
 * Defaults to DENY for every non-essential category. Re-prompts the visitor
 * whenever CONSENT_VERSION is bumped (use this when categories or policy
 * wording materially change).
 *
 * Public API — anything else is implementation detail:
 *   getConsent()                        → current state
 *   setConsent(partial)                 → merge + persist + broadcast + audit
 *   acceptAll()                         → grant everything, audit "accept_all"
 *   rejectAll()                         → strict-necessary only, audit "reject_all"
 *   withdraw()                          → same as rejectAll but audits "withdraw"
 *   openPreferences()                   → dispatch event so banner shows modal
 *   onConsentChange(cb)                 → subscribe; returns unsubscriber
 *   whenGranted(category, loader)       → invoke loader once category turns on
 *   handleGpcSignal()                   → call once on boot; honours navigator.globalPrivacyControl
 */

export const CONSENT_VERSION = "1.0";
export const STORAGE_KEY = `sanctology_consent_v${CONSENT_VERSION.split(".")[0]}`;
export const OPEN_PREFS_EVENT = "sanctology:open-cookie-preferences";
export const CHANGE_EVENT = "sanctology:consent-change";
export const POLICY_URL = "https://sanctology.com/cookies";
export const AUDIT_ENDPOINT = "/api/consent";

export type Category = "strictly_necessary" | "analytics" | "marketing";

export type ConsentMethod =
  | "accept_all"
  | "reject_all"
  | "save_choices"
  | "withdraw"
  | "ccpa_opt_out"
  | "gpc_signal";

export interface ConsentRecord {
  version: string;
  decidedAt: string;        // ISO timestamp of the visitor's decision
  consentId: string;        // opaque id, generated once per visitor decision
  categories: Record<Category, boolean>;
  gpcSignal: boolean;
  method: ConsentMethod;
}

export const CATEGORIES: Array<{
  id: Category;
  label: string;
  required: boolean;
  description: string;
}> = [
  {
    id: "strictly_necessary",
    label: "Strictly necessary",
    required: true,
    description:
      "Required for the site to work — page navigation, security, and remembering your cookie choice. These cannot be switched off.",
  },
  {
    id: "analytics",
    label: "Analytics",
    required: false,
    description:
      "Aggregate, anonymised page-view counts so we know which programs people read about. No personal data, no third-party advertising.",
  },
  {
    id: "marketing",
    label: "Marketing",
    required: false,
    description:
      "Reserved. We don't run advertising or marketing trackers today — this category is here so the choice exists if we ever do.",
  },
];

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

const denyAll = (): Record<Category, boolean> => ({
  strictly_necessary: true,
  analytics: false,
  marketing: false,
});

function safeLocalStorageGet(): ConsentRecord | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (parsed.version !== CONSENT_VERSION) return null; // re-prompt on version bump
    return parsed;
  } catch {
    return null;
  }
}

function safeLocalStorageSet(record: ConsentRecord) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* private mode, quota, etc. — silently degrade */
  }
}

function uuidish(): string {
  // tiny non-crypto id, fine for a consent_id
  return (
    "c_" +
    Math.random().toString(36).slice(2, 10) +
    Date.now().toString(36).slice(-4)
  );
}

let current: ConsentRecord | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getConsent(): ConsentRecord | null {
  if (current) return current;
  current = safeLocalStorageGet();
  return current;
}

export function hasDecided(): boolean {
  return getConsent() !== null;
}

export function isGranted(category: Category): boolean {
  const c = getConsent();
  if (!c) return false;
  return !!c.categories[category];
}

interface SetOptions {
  method: ConsentMethod;
  gpcSignal?: boolean;
}

export function setConsent(
  categories: Partial<Record<Category, boolean>>,
  opts: SetOptions,
): ConsentRecord {
  const previous = getConsent();
  const next: ConsentRecord = {
    version: CONSENT_VERSION,
    decidedAt: new Date().toISOString(),
    consentId: previous?.consentId ?? uuidish(),
    categories: {
      ...denyAll(),
      ...(previous?.categories ?? {}),
      ...categories,
      strictly_necessary: true, // always
    },
    gpcSignal: opts.gpcSignal ?? previous?.gpcSignal ?? false,
    method: opts.method,
  };
  current = next;
  safeLocalStorageSet(next);
  broadcast(next);
  void postAudit(next);
  return next;
}

export function acceptAll() {
  return setConsent(
    { analytics: true, marketing: true },
    { method: "accept_all" },
  );
}

export function rejectAll() {
  return setConsent(
    { analytics: false, marketing: false },
    { method: "reject_all" },
  );
}

export function withdraw() {
  return setConsent(
    { analytics: false, marketing: false },
    { method: "withdraw" },
  );
}

export function openPreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_PREFS_EVENT));
}

// Listen for a Global Privacy Control browser signal exactly once on boot.
// If GPC is on and the visitor hasn't decided yet, we treat it as a strict
// opt-out and record it, but still leave the banner up so they can also
// affirmatively engage if they want.
export function handleGpcSignal() {
  if (typeof navigator === "undefined") return;
  // @ts-expect-error — GPC isn't in lib.dom yet
  const gpc = !!navigator.globalPrivacyControl;
  if (!gpc) return;
  if (hasDecided()) return;
  setConsent(
    { analytics: false, marketing: false },
    { method: "gpc_signal", gpcSignal: true },
  );
}

// ---------------------------------------------------------------------------
// Event bus
// ---------------------------------------------------------------------------

type Listener = (record: ConsentRecord) => void;

function broadcast(record: ConsentRecord) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<ConsentRecord>(CHANGE_EVENT, { detail: record }),
  );
}

export function onConsentChange(cb: Listener): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handler = (e: Event) =>
    cb((e as CustomEvent<ConsentRecord>).detail);
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

/**
 * Convenience: invoke `loader` immediately if the category is already
 * granted; otherwise wait for the next consent change that grants it.
 * The loader is invoked at most once.
 */
export function whenGranted(category: Category, loader: () => void): () => void {
  let fired = false;
  const tryFire = (rec: ConsentRecord | null) => {
    if (fired) return;
    if (rec && rec.categories[category]) {
      fired = true;
      try {
        loader();
      } catch {
        /* swallow loader errors so consent flow never breaks */
      }
    }
  };
  tryFire(getConsent());
  return onConsentChange((rec) => tryFire(rec));
}

// ---------------------------------------------------------------------------
// Audit logging (fire-and-forget; never blocks the UI)
// ---------------------------------------------------------------------------

async function postAudit(record: ConsentRecord): Promise<void> {
  if (typeof window === "undefined") return;
  const payload = {
    event: "consent_update",
    method: record.method,
    timestamp: record.decidedAt,
    consent_version: record.version,
    policy_url: POLICY_URL,
    region: regionHint(),
    categories: record.categories,
    gpc_signal: record.gpcSignal,
    consent_id: record.consentId,
    user_agent: navigator.userAgent,
  };
  const body = JSON.stringify(payload);
  // Prefer sendBeacon — survives unload, doesn't block.
  try {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon?.(AUDIT_ENDPOINT, blob)) return;
  } catch {
    /* fall through to fetch */
  }
  try {
    await fetch(AUDIT_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    });
  } catch {
    /* ignore — visitor's local choice is still respected */
  }
}

function regionHint(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.startsWith("Asia/Kolkata")) return "IN (DPDP)";
    if (tz.startsWith("Europe/")) return "UK/EU (GDPR · prior opt-in)";
    if (tz.startsWith("America/")) return "US (CCPA)";
    return tz || "unknown";
  } catch {
    return "unknown";
  }
}
